import { z } from 'zod';
import { SITE_UPLOAD_KINDS, UPLOAD_KINDS } from '@/utils/api/uploads/kinds';

/**
 * Request a presigned upload URL. `filename` is read for its extension — the
 * stored object is renamed to a UUID — and kept as the default name a
 * consuming endpoint falls back to. `size_bytes` is both checked against the
 * kind's cap and signed into the URL.
 */
/**
 * Control characters are rejected here rather than left to the database.
 * `filename` is stored verbatim in a text column, and Postgres cannot hold a
 * NUL byte in one — so without this a filename containing one passes
 * validation, passes extension extraction, and fails at the insert, turning a
 * malformed request into a 500.
 */
const NO_CONTROL_CHARS = /^[^\u0000-\u001f\u007f]+$/;

export const CreateUploadSchema = z.object({
  kind: z.enum(UPLOAD_KINDS),
  filename: z
    .string()
    .trim()
    .min(1, 'filename is required')
    .max(255)
    .regex(NO_CONTROL_CHARS, 'filename must not contain control characters'),
  size_bytes: z.number().int().positive('size_bytes must be a positive integer'),
});

/**
 * A training carries up to TRAIN_CONCURRENCY_LIMIT (10) outputs, so a full set
 * is the case worth batching for. 25 leaves room above that without making one
 * request able to park an unbounded number of handles.
 */
export const MAX_UPLOADS_PER_REQUEST = 25;

/**
 * Mint several at once. Kinds may be mixed — creating a tone with an image and
 * a set of recordings is one request, not seven.
 *
 * A batch is all-or-nothing: every entry is validated before any row is
 * written, so a caller never has to reconcile a half-minted set.
 */
export const CreateUploadsSchema = z.object({
  uploads: z
    .array(CreateUploadSchema)
    .min(1, 'uploads must not be empty')
    .max(MAX_UPLOADS_PER_REQUEST, `uploads must contain at most ${MAX_UPLOADS_PER_REQUEST} entries`),
});

export type CreateUploadBody = z.infer<typeof CreateUploadSchema>;

/** The site's own uploaders: always a batch, and avatars are allowed. */
export const SiteUploadsSchema = z.object({
  uploads: z
    .array(CreateUploadSchema.extend({ kind: z.enum(SITE_UPLOAD_KINDS) }))
    .min(1, 'uploads must not be empty')
    .max(MAX_UPLOADS_PER_REQUEST, `uploads must contain at most ${MAX_UPLOADS_PER_REQUEST} entries`),
});
export type CreateUploadRequestBody =
  | z.infer<typeof CreateUploadsSchema>
  | z.infer<typeof CreateUploadSchema>;

/** A batch is anything carrying `uploads`, however malformed the rest is. */
function isBatchShape(value: unknown): boolean {
  return typeof value === 'object' && value !== null && 'uploads' in value;
}

function schemaFor(value: unknown) {
  return isBatchShape(value) ? CreateUploadsSchema : CreateUploadSchema;
}

/**
 * Either shape on the same endpoint; the response mirrors what was sent.
 *
 * Dispatched on the presence of `uploads` rather than expressed as a
 * `z.union`. A union that fails every branch reports `invalid_union` with an
 * empty path, which parse-body renders as a bare "Invalid input" — so the two
 * likeliest first-integration mistakes, a typo'd `kind` and a missing
 * `size_bytes`, both came back with no field name at all.
 */
export const CreateUploadRequestSchema: z.ZodType<
  CreateUploadRequestBody,
  z.ZodTypeDef,
  unknown
> = z
  .unknown()
  .superRefine((value, ctx) => {
    const result = schemaFor(value).safeParse(value);
    if (result.success) return;
    for (const issue of result.error.issues) ctx.addIssue(issue);
  })
  .transform((value) => schemaFor(value).parse(value) as CreateUploadRequestBody);

export function isBatchRequest(
  body: CreateUploadRequestBody
): body is z.infer<typeof CreateUploadsSchema> {
  return isBatchShape(body);
}
